import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useLang } from "../context/LangContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { supabase } from "../lib/supabase";
import { HeartPulse, Activity, ClipboardList, BellRing, BarChart3, FileText, Droplet, Moon, Sun, Languages, ChevronRight, Phone, Shield, Utensils, Quote } from "lucide-react";

export default function Landing() {
   const [menuOpen, setMenuOpen] = useState(false);
  const { t, lang, setLang } = useLang();
  const { theme, toggle } = useTheme();


  const features = [
    { icon: Utensils, label: t("feat_tracking") },
    { icon: Activity, label: t("feat_monitor") },
    { icon: ClipboardList, label: t("feat_summary") },
    { icon: BellRing, label: t("feat_reminders") },
    { icon: BarChart3, label: t("feat_notifs") },
    { icon: FileText, label: t("feat_reports") },
  ];

  const steps = [
    { n: "01", title: t("step1_title"), desc: t("step1_desc"), icon: Utensils },
    { n: "02", title: t("step2_title"), desc: t("step2_desc"), icon: Activity },
    { n: "03", title: t("step3_title"), desc: t("step3_desc"), icon: HeartPulse },
  ];

  const stories = [
    { name: t("story1_name"), quote: t("story1_quote") },
    { name: t("story2_name"), quote: t("story2_quote") },
    { name: t("story3_name"), quote: t("story3_quote") },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
{/* Header */}
<header className="sticky top-0 z-50 backdrop-blur bg-background/80 border-b border-border">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 flex items-center justify-between h-16">
    
    {/* Logo */}
    <Link to="/" className="flex items-center gap-3 font-bold text-lg">
      <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-white">
        ❤
      </div>
      Dialysis Diet Pro
    </Link>

    {/* Desktop Nav */}
    <nav className="hidden md:flex items-center gap-6 text-sm">
      <a href="#how" className="hover:text-primary">How It Works</a>
      <a href="#features" className="hover:text-primary">Features</a>
      <a href="#stories" className="hover:text-primary">Stories</a>

    </nav>

    {/* Right side */}
    <div className="flex items-center gap-2">
      <button
  onClick={() => setLang(lang === "ar" ? "en" : "ar")}
  className="text-sm px-2 py-1 border border-border rounded-md"
>
  {lang === "ar" ? "EN" : "AR"}
</button>

      <Link to="/login" className="hidden sm:inline text-sm">Log In</Link>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        className="md:hidden px-3 py-2 border rounded-lg"
      >
        ☰
      </button>
    </div>
  </div>

  {/* Mobile Menu */}
  {menuOpen && (
    <div className="md:hidden px-4 pb-4 flex flex-col gap-3 text-sm">
      <a href="#how">How It Works</a>
      <a href="#features">Features</a>
      <a href="#stories">Stories</a>

    </div>
  )}
</header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-14 pb-16 md:pt-24 md:pb-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className="fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted border border-border text-xs font-semibold uppercase tracking-wider mb-6">
                <Shield className="w-3.5 h-3.5" /> Professional Dialysis Nutrition Tracker
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight" style={{ fontFamily: lang === "ar" ? "Cairo" : "Manrope" }} data-testid="hero-title">
                {t("tagline")}
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-xl">{t("subtagline")}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" className="w-full sm:w-auto"><Button size="lg" className="btn-primary h-12 px-6 rounded-full w-full sm:w-auto" data-testid="hero-get-started">{t("get_started")} <ChevronRight className="w-4 h-4 ms-1" /></Button></Link>
                <Link to="/login" className="w-full sm:w-auto"><Button size="lg" variant="outline" className="h-12 px-6 rounded-full w-full sm:w-auto" data-testid="hero-login">{t("login")}</Button></Link>
              </div>
              <div className="mt-10 grid grid-cols-4 gap-4 text-center">
                {["potassium", "phosphorus", "sodium", "protein"].map((k) => (
                  <div key={k} className="bento-card !p-3 flex flex-col justify-between h-[70px]">
                    <div className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-widest truncate">{t(k)}</div>
                    <div className="mt-1 h-1.5 rounded-full" style={{ background: `var(--rs-${k})` }} />
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl blur-3xl opacity-30" style={{ background: "var(--rs-primary)" }} />
              <img
                src="https://images.pexels.com/photos/8413336/pexels-photo-8413336.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Nurse and senior patient"
                className="relative rounded-3xl border border-border w-full aspect-[4/3] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute bottom-4 start-4 bento-card !p-4 flex items-center gap-3 max-w-[240px]">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--rs-sodium)" }}>
                  <HeartPulse className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">
                    {lang === "ar" ? "حمية اليوم لمرضى غسيل الكلى" : "Today's Dialysis Diet"}
                        </div>
                  <div className="text-sm font-semibold">
                   {lang === "ar" ? "العناصر الغذائية ضمن المعدل الآمن" : "Nutrients within safe range"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-16 md:py-20 border-t border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: lang === "ar" ? "Cairo" : "Manrope" }}>{t("how_it_works")}</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {steps.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="bento-card hover:-translate-y-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{s.n}</span>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--rs-primary)" }}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
                  <p className="text-muted-foreground">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 md:py-20 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: lang === "ar" ? "Cairo" : "Manrope" }}>{t("features")}</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bento-card flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "var(--rs-sodium)22" }}>
                    <Icon className="w-5 h-5" style={{ color: "var(--rs-primary)" }} />
                  </div>
                  <div>
                    <div className="font-semibold">{f.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stories */}
      <section id="stories" className="py-16 md:py-20 border-t border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: lang === "ar" ? "Cairo" : "Manrope" }}>{t("patient_stories")}</h2>
              <img
                src="https://images.pexels.com/photos/7345460/pexels-photo-7345460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Healthcare professional with elderly man"
                className="mt-8 rounded-3xl border border-border w-full aspect-[4/3] object-cover"
              />
            </div>
            <div className="space-y-4">
              {stories.map((s, i) => (
                <div key={i} className="bento-card">
                  <Quote className="w-6 h-6 text-muted-foreground mb-3" />
                  <p className="text-lg leading-relaxed">&ldquo;{s.quote}&rdquo;</p>
                  <div className="mt-4 text-sm font-semibold" style={{ color: "var(--rs-primary)" }}>— {s.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>



      {/* Disclaimer */}
      <section className="py-16 border-t border-border bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 text-center">
          <div className="inline-flex w-12 h-12 rounded-full items-center justify-center mb-4" style={{ background: "var(--rs-sodium)22" }}>
            <Shield className="w-6 h-6" style={{ color: "var(--rs-primary)" }} />
          </div>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto" data-testid="disclaimer-text">
            {t("disclaimer")}
          </p>
          
        </div>
      </section>




{/* Special Thanks */}
      <section className="py-12 border-t border-border text-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 md:px-8">
          <p className="text-xs uppercase tracking-wider text-teal-400 mb-3">
            {lang === "ar" ? "شكر خاص" : "Special Thanks"}
          </p>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            {lang === "ar" ? (
              <>شكر خاص إلى <span className="font-medium text-teal-400/90">مريض غسيل الكلى المهندس أنطوان بعقليني</span> على دعمه وثقته ومساهمته القيّمة في تطوير Dialysis Diet Pro.</>
            ) : (
              <>Special thanks to <span className="font-medium text-teal-400/90">Dialysis Patient Eng. Antoine Baaklini</span> for his support, trust, and valuable contribution to Dialysis Diet Pro.</>
            )}
          </p>
        </div>
      </section>


      {/* Footer */}
      
      <footer className="py-10 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-3 flex-wrap justify-center md:justify-start">
            
            <HeartPulse className="w-4 h-4" />
            <span className="font-semibold">Dialysis Diet Pro</span>
            <span>·</span>
            <span>{t("copyright")}</span>
          </div>
          <div className="flex items-center gap-4">
            <span>{t("powered_by")}</span>
          </div>
        </div>
        <nav aria-label="Legal pages" className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
          <Link to="/terms" className="hover:text-primary">
            {lang === "ar" ? "شروط الاستخدام" : "Terms of Use"}
          </Link>
          <Link to="/privacy" className="hover:text-primary">
            {lang === "ar" ? "سياسة الخصوصية" : "Privacy Policy"}
          </Link>
          <Link to="/medical-disclaimer" className="hover:text-primary">
            {lang === "ar" ? "إخلاء المسؤولية الطبية" : "Medical Disclaimer"}
          </Link>
        </nav>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-4 text-xs text-center text-muted-foreground/80">
          {t("footer_short")}
        </div>
        <p className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 mt-4 text-center text-xs leading-relaxed text-muted-foreground/75">
          Dialysis Diet Pro is designed for educational and tracking purposes only and does not replace professional medical advice, diagnosis, or treatment. Always consult your doctor or renal dietitian.
        </p>
      </footer>
    </div>
  );
}
